import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import logOutIcon from '../assets/icons/log-out.png';
import { fetchPatients, linkPatient, syncAirtable } from '../services/patient';
import { setPatients, setLoading, setError } from '../store/patientSlice';
import useAirtableSyncStatus from '../hooks/useAirtableSyncStatus';

export default function UserMenu({
  fullName,
  email,
  initials = 'IM',
  onLogout,
  isSupervisor = false,
  viewAsRole = 'Patient',
  onSwitchRole,
}) {
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.user.role);
  const { patients = [], loading, error } = useSelector((state) => state.patient);
  const isSupervisorRole = userRole === 'Supervisor';
  const effectiveRole = isSupervisorRole ? (viewAsRole === 'Doctor/Nurse' ? 'Doctor/Nurse' : 'Patient') : userRole;
  const showPatientSelector = isSupervisorRole && effectiveRole === 'Patient';
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [linkingEmail, setLinkingEmail] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [shouldPollSyncStatus, setShouldPollSyncStatus] = useState(false);
  const [syncTaskId, setSyncTaskId] = useState(null);
  const [hasShownSyncCompleteToast, setHasShownSyncCompleteToast] = useState(false);
  const airtableSync = useAirtableSyncStatus({
    enabled: Boolean(shouldPollSyncStatus && showPatientSelector),
    pollIntervalMs: 1500,
    taskId: syncTaskId,
  });
  const isSyncComplete =
    airtableSync.status === 'completed' ||
    airtableSync.statusPayload?.result != null ||
    (typeof airtableSync.progress === 'number' && airtableSync.progress >= 100);


  useEffect(() => {
    if (isSyncComplete && shouldPollSyncStatus && !hasShownSyncCompleteToast) {
      toast.success('Completed 100%', {
        hideProgressBar: true
      }); 

      setIsSyncing(false);

      setTimeout(() => {
        window.location.reload();
      }, 400);

      setHasShownSyncCompleteToast(true);
      setShouldPollSyncStatus(false);
      setIsSyncing(false);
    }
  }, [isSyncComplete, shouldPollSyncStatus, hasShownSyncCompleteToast]);

  useEffect(() => {
    if (isSupervisorRole) {
      const loadPatients = async () => {
        try {
          dispatch(setLoading(true));
          const data = await fetchPatients();
          dispatch(setPatients(data));
        } catch (err) {
          dispatch(setError(err.message || 'Failed to load patients'));
        }
      };
      loadPatients();
    }
  }, [isSupervisorRole, dispatch]);

  const handlePatientSearch = (query) => {
    setEmailSearchQuery(query);
  };

  const getFilteredPatients = () => {
    const terms = (emailSearchQuery || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (terms.length === 0) return patients;
    return patients.filter((patient) => {
      const haystack = [
        patient.label,
        patient.fullName,
        patient.customerId,
        patient.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  };

  const filteredPatients = getFilteredPatients();

  const patientDisplayLabel = (patient) => {
    const myEmail = (email || '').toLowerCase();
    if (patient.role === 'Supervisor' && patient.email === myEmail) {
      return 'terug naar mijzelf';
    }
    return patient.label;
  };

  const handleEmailClick = async (patientEmail) => {
    if (linkingEmail || isDisconnecting) return;

    try {
      setLinkingEmail(patientEmail);
      const response = await linkPatient(patientEmail);

      // Success response
      if (response?.message || response?.patient_email) {
        toast.success(response.message || 'Successfully linked to patient');
        // Reload page after a short delay to show the toast
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      // Handle error responses (400 errors)
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Failed to link patient';
      toast.error(errorMessage);
      setLinkingEmail(null);
    }
  };

  const handleDisconnect = async () => {
    if (linkingEmail || isDisconnecting || !email) return; // Prevent multiple simultaneous requests

    try {
      setIsDisconnecting(true);
      const response = await linkPatient(email);

      // Success response
      if (response?.message || response?.patient_email) {
        toast.success(response.message || 'Successfully disconnected');
        // Reload page after a short delay to show the toast
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      // Handle error responses (400 errors)
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Failed to disconnect';
      toast.error(errorMessage);
      setIsDisconnecting(false);
    }
  };

  const handleSyncAirtable = async (e) => {
    e?.preventDefault?.();
    if (isSyncing || linkingEmail || isDisconnecting) return;

    try {
      setIsSyncing(true);
      setShouldPollSyncStatus(true);
      setSyncTaskId(null);
      setHasShownSyncCompleteToast(false);
      const response = await syncAirtable();

      // Success response
      if (response) {
        const taskId =
          response.job_id ||
          response.customer_job_id ||
          response.blood_job_id ||
          response.customer_task_id ||
          response.blood_task_id ||
          response.task_id;
        if (taskId) {
          setSyncTaskId(taskId);
        } else {
          toast.info('Sync started, but no job id returned for status tracking.');
        }
      }
      setIsSyncing(false);
      airtableSync.refresh();
    } catch (error) {
      // Handle error responses
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Failed to sync Airtable';
      toast.error(errorMessage);
      setIsSyncing(false);
    }
  };

  return (
    <div
      className="hidden md:block absolute right-0 top-full mt-3 w-[360px] rounded-2xl border border-gray-200 bg-white shadow-xl z-50"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-orange-400 text-white flex items-center justify-center font-poppins text-base font-semibold">
            {initials}
          </div>
          <div className="flex flex-col">
            <span className="text-[#2B3133] font-poppins text-base font-medium">
              {fullName}
            </span>
            <span className="text-[#889696] font-poppins text-sm">{email}</span>
          </div>
        </div>
        <div className="mt-5 h-px bg-gray-200" />

        {isSupervisor && onSwitchRole && (
          <div className="mt-4">
            <h3 className="text-[#2B3133] font-poppins text-sm font-semibold mb-3">
              View as
            </h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onSwitchRole('Patient')}
                className={`flex-1 font-poppins text-sm py-2.5 px-4 rounded-lg border transition-colors ${effectiveRole === 'Patient'
                    ? 'bg-[#119975] text-white border-[#119975]'
                    : 'bg-white text-[#2B3133] border-[#CBD5E0] hover:bg-[#F8F8F8]'
                  }`}
              >
                Patient
              </button>
              <button
                type="button"
                onClick={() => onSwitchRole('Doctor/Nurse')}
                className={`flex-1 font-poppins text-sm py-2.5 px-4 rounded-lg border transition-colors ${effectiveRole === 'Doctor/Nurse'
                    ? 'bg-[#119975] text-white border-[#119975]'
                    : 'bg-white text-[#2B3133] border-[#CBD5E0] hover:bg-[#F8F8F8]'
                  }`}
              >
                Doctor/Nurse
              </button>
            </div>
          </div>
        )}

        {showPatientSelector && (
          <div className="mt-4">
            <h3 className="text-[#2B3133] font-poppins text-sm font-semibold mb-3">
              Patients ({patients.length})
            </h3>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin h-5 w-5 border-2 border-[#119975] border-t-transparent rounded-full" />
              </div>
            ) : error ? (
              <p className="text-red-500 font-poppins text-xs py-2">{error}</p>
            ) : patients.length > 0 ? (
              <div className="border border-[#CBD5E0] rounded-xl overflow-hidden bg-white">
                <div className="p-2 border-b border-[#EDEEF0] bg-[#F8F8F8]">
                  <input
                    type="text"
                    placeholder="Search by name or customer number..."
                    value={emailSearchQuery}
                    onChange={(e) => handlePatientSearch(e.target.value)}
                    className="w-full h-[40px] px-3 rounded-lg border border-[#CBD5E0] bg-white outline-none focus:border-[#119975] focus:ring-1 focus:ring-[#119975] text-sm font-poppins"
                  />
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map((patient) => (
                      <div
                        key={patient.email}
                        onClick={() => handleEmailClick(patient.email)}
                        className={`text-[#2B3133] font-poppins text-xs py-2.5 px-4 hover:bg-[#F8F8F8] transition-colors border-b border-[#EDEEF0] last:border-b-0 ${linkingEmail === patient.email
                            ? 'bg-[#E7F6EF] cursor-wait opacity-75'
                            : 'cursor-pointer'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{patientDisplayLabel(patient)}</span>
                          {linkingEmail === patient.email && (
                            <div className="animate-spin h-3 w-3 border-2 border-[#119975] border-t-transparent rounded-full ml-2" />
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-sm text-[#889696] text-center font-poppins">
                      No patients found
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-[#889696] font-poppins text-xs py-2">No patients found</p>
            )}
          </div>
        )}

        {showPatientSelector && (
          <>
            <div className="mt-4 h-px bg-gray-200" />
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="mt-4 w-full flex items-center justify-center gap-2 text-[#2B3133] font-poppins text-base hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg border border-[#CBD5E0] bg-white hover:bg-[#F8F8F8] transition-colors"
            >
              {isDisconnecting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-[#119975] border-t-transparent rounded-full" />
                  <span>Disconnecting...</span>
                </>
              ) : (
                <span>Disconnect</span>
              )}
            </button>
            <button
              type="button"
              onClick={(e) => handleSyncAirtable(e)}
              disabled={isSyncing || shouldPollSyncStatus || airtableSync.isRunning || linkingEmail || isDisconnecting}
              className="mt-3 w-full flex items-center justify-center gap-2 text-[#2B3133] font-poppins text-base hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg border border-[#CBD5E0] bg-white hover:bg-[#F8F8F8] transition-colors"
            >
              {(isSyncing || airtableSync.isRunning) && (
                <div className="animate-spin h-4 w-4 border-2 border-[#119975] border-t-transparent rounded-full" />
              )}
              <span>Sync Airtable</span>
            </button>
            {shouldPollSyncStatus && !isSyncComplete && (
              <div className="mt-3">
                <div className="flex items-center justify-end text-[11px] font-poppins text-[#6B7280] mb-2">
                  <span>{airtableSync.progress != null ? `${Math.round(airtableSync.progress)}%` : ''}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-[#E5E7EB] overflow-hidden">
                  {airtableSync.progress != null ? (
                    <div
                      className="h-full bg-[#119975] transition-all duration-500 ease-out"
                      style={{ width: `${Math.max(0, Math.min(100, airtableSync.progress))}%` }}
                    />
                  ) : (
                    <div className="h-full w-0 bg-[#119975]" />
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {(isSupervisor && onSwitchRole) || showPatientSelector ? <div className="mt-4 h-px bg-gray-200" /> : null}

        <button
          onClick={onLogout}
          className="mt-4 w-full flex items-center gap-3 text-[#2B3133] font-poppins text-base hover:opacity-90"
        >
          <img src={logOutIcon} alt="logout" className="w-5 h-5" />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  );
}
